import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { webObservability } from '@web/lib/observability';
import { useState } from 'react';

import { TradingShellScreen } from '../../components/TradingShellScreen';
import {
  persistChartInterval,
  readPersistedChartInterval,
} from './-workstation/chart/chartIntervalStorage';
import {
  readTradeWorkbenchPreferences,
  TRADE_WORKBENCH_PREFERENCES_STORAGE_KEY,
} from './-workstation/hooks/tradeWorkbenchPreferences';
import { loadTradeWorkbenchRouteData } from './-workstation/loadTradeWorkbenchRouteData';
import { MARKET_DEFAULT_TRADE_COUNT } from './-workstation/market-feed/marketFeedDefaults';
import {
  marketChartBarsQueryOptions,
  marketOrderBookQueryOptions,
  marketSummaryQueryOptions,
  marketsQueryOptions,
  marketTradesQueryOptions,
} from './-workstation/market-feed/marketFeedQueryOptions';
import { getMarketFeedStreamSession } from './-workstation/market-feed/marketFeedStreamSession';
import { TradeWorkbench } from './-workstation/TradeWorkbench';
import type { MarketDefinitionT } from './-workstation/types';

function TradeAssetPage() {
  const { market, markets, requestedAsset } = Route.useLoaderData();

  if (market === null) {
    return (
      <TradingShellScreen
        asset={requestedAsset}
        message="No market is currently available."
      />
    );
  }

  return (
    <TradeWorkbenchWithInterval
      key={market.asset}
      market={market}
      markets={markets}
    />
  );
}

function TradeWorkbenchWithInterval(input: {
  market: MarketDefinitionT;
  markets: MarketDefinitionT[];
}) {
  const [chartInterval, setChartInterval] = useState(() =>
    readPersistedChartInterval(input.market),
  );

  return (
    <TradeWorkbench
      chartInterval={chartInterval}
      market={input.market}
      markets={input.markets}
      onChartIntervalChange={(nextInterval) => {
        setChartInterval(nextInterval);
        persistChartInterval(nextInterval);
      }}
    />
  );
}

export const Route = createFileRoute('/trade/$asset')({
  loader: async ({ context, params }) => {
    const routeData = await loadTradeWorkbenchRouteData({
      asset: params.asset,
      queryClient: context.queryClient,
    });

    if (
      routeData.market !== null &&
      routeData.market.asset !== routeData.requestedAsset
    ) {
      throw redirect({
        params: {
          asset: routeData.market.asset,
        },
        replace: true,
        to: '/trade/$asset',
      });
    }

    if (routeData.market === null && routeData.markets[0] !== undefined) {
      if (routeData.markets[0].asset !== routeData.requestedAsset) {
        throw redirect({
          replace: true,
          params: {
            asset: routeData.markets[0].asset,
          },
          to: '/trade/$asset',
        });
      }
    }

    if (routeData.market !== null) {
      getMarketFeedStreamSession({
        asset: routeData.market.asset,
        maxTradeCount: MARKET_DEFAULT_TRADE_COUNT,
      }).preconnect();

      warmTradeWorkbenchRouteData({
        market: routeData.market,
        queryClient: context.queryClient,
      }).catch(captureTradeWorkbenchWarmRouteDataError);
    }

    return routeData;
  },
  component: TradeAssetPage,
});

async function warmTradeWorkbenchRouteData(input: {
  market: MarketDefinitionT;
  queryClient: QueryClient;
}) {
  const chartInterval = readWarmupChartInterval(input.market);
  const warmupQueries = [
    input.queryClient.prefetchQuery(
      marketChartBarsQueryOptions(input.market.asset, chartInterval),
    ),
    input.queryClient.prefetchQuery(
      marketSummaryQueryOptions(input.market.asset),
    ),
    input.queryClient.prefetchQuery(marketsQueryOptions()),
    warmVisibleSidePanelRouteData({
      market: input.market,
      queryClient: input.queryClient,
    }),
  ];

  await Promise.allSettled(warmupQueries);
}

function captureTradeWorkbenchWarmRouteDataError(error: unknown) {
  webObservability.captureException(error, {
    tags: {
      operation: 'trade_workstation.warm_route_data',
    },
  });
}

function readWarmupChartInterval(
  market: MarketDefinitionT,
): ReturnType<typeof readPersistedChartInterval> {
  if (typeof window === 'undefined') {
    return market.defaultChartInterval;
  }

  return readPersistedChartInterval(market);
}

async function warmVisibleSidePanelRouteData(input: {
  market: MarketDefinitionT;
  queryClient: QueryClient;
}) {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.matchMedia('(max-width: 1024px)').matches) {
    return;
  }

  const preferences = readTradeWorkbenchPreferences({
    market: input.market,
    storageValue: window.localStorage.getItem(
      TRADE_WORKBENCH_PREFERENCES_STORAGE_KEY,
    ),
  });

  if (preferences.sidebarTab === 'trades') {
    await input.queryClient.prefetchQuery(
      marketTradesQueryOptions(input.market.asset),
    );
    return;
  }

  await input.queryClient.prefetchQuery(
    marketOrderBookQueryOptions(
      input.market.asset,
      preferences.orderBookGrouping,
    ),
  );
}
